import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ErrorApi } from '../lib/api';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Marca } from '../componentes/Marca';
import { PanelPresentacion } from '../componentes/PanelPresentacion';
import { Check, Correo, Reloj } from '../componentes/iconos';

/**
 * Pantalla a la que apunta el enlace del correo.
 *
 * El enlace es un GET a esta página, pero la verificación se hace con un POST
 * desde acá. Un GET que cambia estado lo dispara cualquier previsualizador
 * —el de WhatsApp, el antivirus del correo corporativo—, y la persona recibiría
 * un enlace ya consumido sin haberlo abierto nunca.
 */
export function VerificarCorreo() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';

  const [estado, setEstado] = useState<'verificando' | 'listo' | 'falla'>('verificando');
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [mensaje, setMensaje] = useState('');
  const yaCorrio = useRef(false);

  useEffect(() => {
    // El token es de un solo uso: en modo estricto React monta dos veces, y sin
    // esta guarda el segundo intento quemaría el token y mostraría un error.
    if (yaCorrio.current) return;
    yaCorrio.current = true;

    if (!token) {
      setEstado('falla');
      setMensaje('El enlace está incompleto. Ábrelo directamente desde el correo.');
      return;
    }

    void (async () => {
      try {
        const r = await api.post<{ empresaNombre: string }>('/api/auth/verificar', { token });
        setEmpresaNombre(r.empresaNombre);
        setEstado('listo');
      } catch (err) {
        setMensaje(
          err instanceof ErrorApi
            ? err.message
            : 'No pudimos conectar con el servidor. Intenta de nuevo.',
        );
        setEstado('falla');
      }
    })();
  }, [token]);

  return (
    <div className="flex min-h-screen bg-white">
      <PanelPresentacion />
      <main className="flex min-w-0 flex-1 items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-[22rem]">
          <div className="mb-10 lg:hidden">
            <Marca tamano="md" />
          </div>

          {estado === 'verificando' && (
            <div className="flex items-center gap-3 text-marino-500">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-marino-200 border-t-naranja-500" />
              Confirmando tu correo…
            </div>
          )}

          {estado === 'listo' && (
            <div>
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-green-50 text-green-700">
                <Check size={24} />
              </div>
              <h1 className="text-[1.75rem] leading-tight font-bold text-marino-800">
                Correo confirmado
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-marino-500">
                Tu solicitud para <strong className="text-marino-800">{empresaNombre}</strong> ya
                está en revisión.
              </p>

              <div className="mt-6 flex gap-4 rounded-2xl border border-marino-100 bg-marino-50/60 p-5">
                <span className="mt-0.5 shrink-0 text-naranja-500">
                  <Reloj size={20} />
                </span>
                <div>
                  <div className="text-sm font-semibold text-marino-800">Falta un paso</div>
                  <p className="mt-1 text-sm leading-relaxed text-marino-500">
                    Copathy tiene que aprobar tu acceso.{' '}
                    <strong className="text-marino-700">Todavía no puedes iniciar sesión.</strong>{' '}
                    Te avisamos por correo en cuanto esté listo.
                  </p>
                </div>
              </div>

              <Link
                to="/entrar"
                className="mt-7 inline-block text-sm font-medium text-marino-500 hover:text-naranja-600"
              >
                Volver al inicio
              </Link>
            </div>
          )}

          {estado === 'falla' && <PanelReenvio mensaje={mensaje} />}
        </div>
      </main>
    </div>
  );
}

/** Un enlace vencido no es un callejón sin salida: se pide otro desde acá. */
function PanelReenvio({ mensaje }: { mensaje: string }) {
  const [correo, setCorreo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  async function reenviar() {
    setEnviando(true);
    try {
      await api.post('/api/auth/reenviar-verificacion', { correo });
      setEnviado(true);
    } catch {
      // El endpoint siempre responde 204: no hay nada que informar acá.
      setEnviado(true);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div>
      <h1 className="text-[1.75rem] leading-tight font-bold text-marino-800">
        Ese enlace ya no sirve
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-marino-500">{mensaje}</p>

      {enviado ? (
        <div className="mt-6 flex gap-4 rounded-2xl border border-marino-100 bg-marino-50/60 p-5">
          <span className="mt-0.5 shrink-0 text-naranja-500">
            <Correo size={20} />
          </span>
          <p className="text-sm leading-relaxed text-marino-500">
            Si ese correo tiene una solicitud sin confirmar, te acaba de llegar un enlace nuevo.
          </p>
        </div>
      ) : (
        <div className="mt-6">
          <Campo
            etiqueta="Tu correo"
            type="email"
            autoComplete="email"
            icono={<Correo size={18} />}
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
          />
          <Boton
            onClick={() => void reenviar()}
            cargando={enviando}
            disabled={correo.length < 5}
            className="mt-1 w-full"
          >
            Enviarme otro enlace
          </Boton>
        </div>
      )}

      <Link
        to="/entrar"
        className="mt-7 inline-block text-sm font-medium text-marino-500 hover:text-naranja-600"
      >
        Volver al inicio
      </Link>
    </div>
  );
}
